import { useState, useCallback, useRef } from "react";

const fmt = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v || 0);
const pct = (v) => `${(parseFloat(v) || 0).toFixed(1)}%`;

// ─── FORMULA DEFINITIONS ───────────────────────────────────────────────
const FORMULAS = {
  investorReturn: {
    label: "Investor Return",
    f: "ARV × 10% (standard investor return rate)",
    vars: (inputs, c) => [
      ["ARV", fmt(inputs.arv)],
      ["Rate", "10%"],
      ["Result", fmt(c.investorReturn)],
    ],
  },
  totalRepairs: {
    label: "Total Repairs (Subject)",
    f: "Sum of all Subject values in rehab table",
    vars: (inputs, c) =>
      inputs.rehabExpenses
        .filter((x) => x.subject > 0)
        .map((x) => [x.re, fmt(x.subject)])
        .concat([["Total", fmt(c.totalSubjectRepairs)]]),
  },
  contingency: {
    label: "Contingency Reserve",
    f: "Total Repairs × Contingency %",
    vars: (inputs, c) => [
      ["Total Repairs", fmt(c.totalSubjectRepairs)],
      ["Contingency %", pct(inputs.contingencyPct)],
      ["Result", fmt(c.contingency)],
    ],
  },
  totalRehabBudget: {
    label: "Total Rehab Budget",
    f: "Total Repairs + Contingency Reserve",
    vars: (inputs, c) => [
      ["Total Repairs", fmt(c.totalSubjectRepairs)],
      ["Contingency", fmt(c.contingency)],
      ["Result", fmt(c.totalRehabBudget)],
    ],
  },
  cashOffer: {
    label: "Cash Offer",
    f: "((0.95 × ARV) − Investor Return − Repairs − Desired Profit) ÷ 1.01",
    vars: (inputs, c) => [
      ["ARV", fmt(inputs.arv)],
      ["95% ARV", fmt(inputs.arv * 0.95)],
      ["Investor Return", fmt(c.investorReturn)],
      ["Repairs", fmt(inputs.repairs)],
      // ["Repairs (Subject Total)", fmt(c.totalSubjectRepairs)],
      ["Desired Profit", fmt(inputs.desiredProfit)],
      ["Result", fmt(c.cashOffer)],
    ],
  },
};

// ─── SUB‑COMPONENTS (Tailwind) ────────────────────────────────────────
function FormulaCell({ id, value, computed, inputs }) {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);
  const def = FORMULAS[id];
  if (!def) return <span className="text-sm font-medium text-gray-900">{value}</span>;

  const vars = def.vars(inputs, computed);

  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => setShow(true), 300);
  };
  const handleMouseLeave = () => {
    clearTimeout(timerRef.current);
    setShow(false);
  };

  return (
    <div className="relative inline-block">
      <button onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 text-sm font-medium text-blue-700 flex items-center gap-1 hover:bg-blue-100 transition-colors" type="button">
        {value}
      </button>
      {show && (
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-3 min-w-[240px] text-left">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-gray-800">{def.label}</span>
          </div>
          <div className="bg-gray-50 rounded-md p-2 text-[11px] font-mono text-gray-600 border border-gray-100 mb-2">{def.f}</div>
          <div className="flex flex-col gap-1">
            {vars.map(([k, v]) => (
              <div key={k} className="flex justify-between text-[11px]">
                <span className="text-gray-500">{k}</span>
                <span className={k === "Result" || k === "Total" ? "font-medium text-blue-700" : "text-gray-800"}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, color }) {
  return (
    <div className="text-white text-[11px] font-medium tracking-wide uppercase rounded-md px-2.5 py-1 mt-3 mb-1.5" style={{ backgroundColor: color }}>
      {title}
    </div>
  );
}

function ResultRow({ label, children, bold }) {
  return (
    <div className={`flex items-center justify-between py-1 border-b border-gray-100 ${bold ? "font-medium" : ""}`}>
      <span className={`text-xs ${bold ? "text-gray-900" : "text-gray-600"}`}>{label}</span>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, prefix, suffix, type = "number", step, min = 0 }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
      <label className="text-xs text-gray-600 flex-shrink-0 max-w-[55%]">{label}</label>
      <div className="flex items-center gap-1">
        {prefix && <span className="text-xs text-gray-400">{prefix}</span>}
        <input type={type} value={value} min={min} step={step || 1} onChange={(e) => onChange(type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)} className="w-[90px] text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
        {suffix && <span className="text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

function LinkedAmountInput({ label, baseValue, amount, onAmountChange, step = 1000, min = 0 }) {
  const percent = baseValue > 0 ? (amount / baseValue) * 100 : 0;
  const handleDollarChange = (newDollar) => {
    const dollarValue = parseFloat(newDollar) || 0;
    onAmountChange(dollarValue);
  };
  const handlePercentChange = (newPercent) => {
    const percentValue = parseFloat(newPercent) || 0;
    const newDollar = (percentValue / 100) * baseValue;
    onAmountChange(newDollar);
  };
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-gray-100">
      <label className="text-xs text-gray-600 flex-shrink-0">{label}</label>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">$</span>
          <input type="number" value={amount} min={min} step={step} onChange={(e) => handleDollarChange(e.target.value)} className="w-[90px] text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
        </div>
        <span className="text-xs text-gray-400">–</span>
        <div className="flex items-center gap-1">
          <input type="number" value={percent.toFixed(1)} min={0} max={100} step={0.5} onChange={(e) => handlePercentChange(e.target.value)} className="w-12 text-right text-xs py-1 px-2 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400" />
          <span className="text-xs text-gray-400">%</span>
        </div>
      </div>
    </div>
  );
}

// ─── DEFAULT REHAB EXPENSES ────────────────────────────────────────────
const DEFAULT_REHAB_EXPENSES = [
  { re: "Kitchen", subject: 25000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Appliances", subject: 4500, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Bathrooms", subject: 24000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Roof", subject: 15000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Windows", subject: 10000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Garage Door", subject: 2000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Exterior Paint", subject: 5000, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Interior Paint", subject: 4500, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Flooring", subject: 11860, comp1: 0, comp2: 0, comp3: 0 },
  { re: "HVAC", subject: 7500, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Water Heater", subject: 1200, comp1: 0, comp2: 0, comp3: 0 },
  { re: "Foundation", subject: 0, comp1: 0, comp2: 0, comp3: 0 },
  { re: "MISC", subject: 15000, comp1: 0, comp2: 0, comp3: 0 },
];

export default function CashOfferCalculator() {
  const [inputs, setInputs] = useState({
    address: "",
    arv: 620000,
    desiredProfit: 30000,
    repairs: 20000,
    contingencyPct: 10,
    rehabExpenses: DEFAULT_REHAB_EXPENSES.map((r) => ({ ...r })),
  });

  const set = useCallback((k) => (v) => setInputs((p) => ({ ...p, [k]: v })), []);

  // Handlers for dynamic rehab expense rows
  const updateRehabExpense = (index, field, value) => {
    setInputs((p) => ({
      ...p,
      rehabExpenses: p.rehabExpenses.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const removeRehabExpense = (index) => {
    setInputs((p) => ({
      ...p,
      rehabExpenses: p.rehabExpenses.filter((_, i) => i !== index),
    }));
  };

  const addRehabExpense = () => {
    setInputs((p) => ({
      ...p,
      rehabExpenses: [...p.rehabExpenses, { re: "New Item", subject: 0, comp1: 0, comp2: 0, comp3: 0 }],
    }));
  };

  // Derived values
  const totalSubjectRepairs = inputs.rehabExpenses.reduce((a, r) => a + (r.subject || 0), 0);
  const totalComp1 = inputs.rehabExpenses.reduce((a, r) => a + (r.comp1 || 0), 0);
  const totalComp2 = inputs.rehabExpenses.reduce((a, r) => a + (r.comp2 || 0), 0);
  const totalComp3 = inputs.rehabExpenses.reduce((a, r) => a + (r.comp3 || 0), 0);

  const contingency = (totalSubjectRepairs * inputs.contingencyPct) / 100;
  const totalRehabBudget = totalSubjectRepairs + contingency;
  const investorReturn = inputs.arv * 0.1;
  const arvDiscounted = inputs.arv * 0.95;
  // const cashOffer = (arvDiscounted - investorReturn - totalSubjectRepairs - inputs.desiredProfit) / 1.01;
  const cashOffer = (arvDiscounted - investorReturn - inputs.repairs - inputs.desiredProfit) / 1.01;

  const c = { investorReturn, totalSubjectRepairs, contingency, totalRehabBudget, cashOffer };

  return (
    <div className="font-sans max-w-4xl mx-auto px-4 pb-8">
      {/* Header */}
      <div className="py-3 px-4 border-b border-gray-200 mb-2">
        <div className="text-base font-semibold text-gray-900 mb-1">Cash Offer Calculator</div>
      </div>

      <div className="px-4">
        {/* Max Allowable Offer */}
        {/* <SectionHeader title="Max Allowable Offer" color="#185FA5" />
        <Input label="ARV" prefix="$" value={inputs.arv} onChange={set("arv")} />
        <Input label="Desired Profit" prefix="$" value={inputs.desiredProfit} onChange={set("desiredProfit")} />
        <ResultRow label="Investor Return (10% of ARV)">
          <FormulaCell id="investorReturn" value={fmt(investorReturn)} computed={c} inputs={inputs} />
        </ResultRow>
        <ResultRow label="Cash Offer" bold>
          <FormulaCell id="cashOffer" value={fmt(cashOffer)} computed={c} inputs={inputs} />
        </ResultRow> */}

        <SectionHeader title="Max Allowable Offer" color="#185FA5" />
        <Input label="ARV" prefix="$" value={inputs.arv} onChange={set("arv")} />
        <LinkedAmountInput label="Desired Profit" baseValue={inputs.arv} amount={inputs.desiredProfit} onAmountChange={set("desiredProfit")} step={100} />
        <Input label="Repairs" prefix="$" value={inputs.repairs} onChange={set("repairs")} />
        <LinkedAmountInput label="Investor Return" baseValue={inputs.arv} amount={investorReturn} onAmountChange={set("investorReturn")} step={100} />
        <ResultRow label="Cash Offer" bold>
          <FormulaCell id="cashOffer" value={fmt(cashOffer)} computed={c} inputs={inputs} />
        </ResultRow>

        {/* Rehab Expenses Table */}
        <SectionHeader title="Rehab Expenses" color="#3B6D11" />
        <div className="mt-2 bg-white border border-gray-200 rounded-md overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[minmax(0,2fr)_80px_80px_80px_80px_32px] gap-1 py-1 px-2 bg-gray-100 text-[9px] font-semibold text-gray-400 uppercase border-b border-gray-200">
            <span>Rehab Expense</span>
            <span className="text-center">Subject</span>
            <span className="text-center">Comp 1</span>
            <span className="text-center">Comp 2</span>
            <span className="text-center">Comp 3</span>
            <span></span> {/* for remove button */}
          </div>

          {/* Rows */}
          {inputs.rehabExpenses.map((item, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,2fr)_80px_80px_80px_80px_32px] gap-1 py-0.5 px-2 border-b border-gray-100 items-center text-[10px]">
              <input type="text" value={item.re} onChange={(e) => updateRehabExpense(i, "re", e.target.value)} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded" placeholder="Expense name" />
              <input type="number" value={item.subject || ""} min={0} onChange={(e) => updateRehabExpense(i, "subject", parseFloat(e.target.value) || 0)} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
              <input type="number" value={item.comp1 || ""} min={0} onChange={(e) => updateRehabExpense(i, "comp1", parseFloat(e.target.value) || 0)} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
              <input type="number" value={item.comp2 || ""} min={0} onChange={(e) => updateRehabExpense(i, "comp2", parseFloat(e.target.value) || 0)} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
              <input type="number" value={item.comp3 || ""} min={0} onChange={(e) => updateRehabExpense(i, "comp3", parseFloat(e.target.value) || 0)} className="w-full text-[10px] px-1 py-0.5 border border-gray-200 rounded text-right" />
              <button onClick={() => removeRehabExpense(i)} className="text-red-500 hover:text-red-700 text-lg leading-none p-0 flex items-center justify-center" title="Remove item">
                ✕
              </button>
            </div>
          ))}

          {/* Add Row Button */}
          <div className="px-2 py-1">
            <button onClick={addRehabExpense} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
              <span className="text-lg leading-none">+</span> Add Expense
            </button>
          </div>

          {/* Totals Row */}
          <div className="grid grid-cols-[minmax(0,2fr)_80px_80px_80px_80px_32px] gap-1 py-1 px-2 bg-gray-50 border-t border-gray-200 items-center text-[10px]">
            <span className="font-semibold text-gray-600">TOTAL</span>
            <div className="text-right font-medium text-gray-900">{fmt(totalSubjectRepairs)}</div>
            <div className="text-right font-medium text-gray-900">{fmt(totalComp1)}</div>
            <div className="text-right font-medium text-gray-900">{fmt(totalComp2)}</div>
            <div className="text-right font-medium text-gray-900">{fmt(totalComp3)}</div>
            <span></span>
          </div>
        </div>

        {/* Contingency & Total Rehab Budget */}
        {/* <div className="mt-4 bg-gray-50 rounded-md p-3">
          <div className="flex items-center justify-between py-1 border-b border-gray-200">
            <label className="text-xs text-gray-600">Contingency Reserve</label>
            <div className="flex items-center gap-2">
              <input type="number" value={inputs.contingencyPct} step={1} min={0} max={100} onChange={(e) => set("contingencyPct")(parseFloat(e.target.value) || 0)} className="w-12 text-xs text-right px-1 py-0.5 border border-gray-200 rounded" />
              <span className="text-xs text-gray-400">%</span>
              <FormulaCell id="contingency" value={fmt(contingency)} computed={c} inputs={inputs} />
            </div>
          </div>
          <div className="flex items-center justify-between py-2 font-medium">
            <span className="text-xs text-gray-700">Total Rehab Budget</span>
            <FormulaCell id="totalRehabBudget" value={fmt(totalRehabBudget)} computed={c} inputs={inputs} />
          </div>
        </div> */}
      </div>
    </div>
  );
}
